import { MAX_VIDEO_BYTES } from "@hightouch/lib/agents/agents-orchestration/catalog-upload-constants";

/** The media kinds a user can upload to a video ad's timeline or asset library. */
export const UPLOADABLE_MEDIA_KINDS = ["video", "audio", "image"] as const;
export type UploadableMediaKind = (typeof UPLOADABLE_MEDIA_KINDS)[number];

/**
 * Content types a manual upload may carry. Video is limited to the containers
 * the browser preview and the Remotion renderer both play; audio mirrors that
 * with the common browser-decodable formats, including the vendor-prefixed
 * names some browsers report for wav and m4a files. SVG is left out: the
 * file is served from our origin, and an SVG can carry script.
 */
export const AD_ASSET_MEDIA_CONTENT_TYPES: ReadonlySet<string> = new Set([
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/aac",
  "audio/mp4",
  "audio/x-m4a",
  "audio/ogg",
  "audio/webm",
  "audio/flac",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);

/** Same ceiling as a catalog video, which is the renderer's download budget. */
export const MAX_AD_ASSET_MEDIA_BYTES = MAX_VIDEO_BYTES;

/**
 * Which kind of media a content type is, or `null` when the type is not one
 * the upload endpoints accept.
 */
export function mediaKindForContentType(
  contentType: string,
): UploadableMediaKind | null {
  if (!AD_ASSET_MEDIA_CONTENT_TYPES.has(contentType)) return null;
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("image/")) return "image";
  return "audio";
}

/**
 * Browsers report a type we do not accept for some media - an empty string, a
 * vendor spelling like `audio/x-flac`, or `application/octet-stream` where the
 * OS has no mapping - so the extension decides whenever the type is unusable.
 */
const MEDIA_CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  ".mp4": "video/mp4",
  ".m4v": "video/mp4",
  ".mov": "video/quicktime",
  ".webm": "video/webm",
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
  ".m4a": "audio/mp4",
  ".aac": "audio/aac",
  ".ogg": "audio/ogg",
  ".oga": "audio/ogg",
  ".flac": "audio/flac",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
};

/** The extensions of the audio formats the upload endpoints accept, dot included. */
export const AUDIO_FILE_EXTENSIONS: readonly string[] = Object.entries(
  MEDIA_CONTENT_TYPE_BY_EXTENSION,
)
  .filter(([, contentType]) => mediaKindForContentType(contentType) === "audio")
  .map(([extension]) => extension);

/** The content type a file extension (dot included) implies, or `null` when it is not an accepted media format. */
export function mediaContentTypeForExtension(extension: string): string | null {
  const normalized = extension.toLowerCase();
  return Object.hasOwn(MEDIA_CONTENT_TYPE_BY_EXTENSION, normalized)
    ? (MEDIA_CONTENT_TYPE_BY_EXTENSION[normalized] ?? null)
    : null;
}

export function inferMediaContentType(file: {
  name: string;
  type: string;
}): string | null {
  if (AD_ASSET_MEDIA_CONTENT_TYPES.has(file.type)) return file.type;
  const match = /\.[^.]+$/.exec(file.name);
  if (!match) return null;
  return mediaContentTypeForExtension(match[0]);
}
